/**
 * Space hierarchy tab - displays a collapsible tree of locations for a plot or project.
 */

import { useMemo, useState } from "react";
import type { Location } from "@/types";
import { ChevronRightIcon, ChevronDownIcon, ProjectIcon, SpaceIcon } from "@/lib/icons";

interface SpaceHierarchyTabProps {
  locations: Location[];
  /** Optional: group by plot name when showing project-level hierarchy */
  plotLabel?: string;
  /** Hide the empty state message - useful when parent already shows one */
  hideEmptyState?: boolean;
}

interface LocationNode {
  location: Location;
  children: LocationNode[];
}

function buildTree(locations: Location[]): LocationNode[] {
  const locationMap = new Map<string, Location>();
  const childrenMap = new Map<string, LocationNode[]>();

  for (const loc of locations) {
    locationMap.set(loc.id, loc);
  }

  // Group children by parentLocationId
  const roots: LocationNode[] = [];

  for (const loc of locations) {
    const node: LocationNode = { location: loc, children: [] };

    if (
      loc.parentLocationId &&
      locationMap.has(loc.parentLocationId)
    ) {
      const siblings = childrenMap.get(loc.parentLocationId) || [];
      siblings.push(node);
      childrenMap.set(loc.parentLocationId, siblings);
    } else {
      roots.push(node);
    }
  }

  // Attach children to their parents
  function attachChildren(node: LocationNode): LocationNode {
    const children = childrenMap.get(node.location.id) || [];
    node.children = children.map(attachChildren);
    return node;
  }

  return roots.map(attachChildren);
}

function LocationNodeItem({
  node,
  depth,
  defaultExpanded = false,
}: {
  node: LocationNode;
  depth: number;
  defaultExpanded?: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const Icon = hasChildren ? ProjectIcon : SpaceIcon;

  return (
    <div>
      <div
        className="flex items-center gap-1.5 py-1 text-sm hover:bg-muted/50 rounded px-1 cursor-pointer"
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {hasChildren && (
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            {isExpanded ? (
              <ChevronDownIcon className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="h-3 w-3 text-muted-foreground" />
            )}
          </span>
        )}
        {!hasChildren && <span className="w-3.5" />}
        <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <span className="truncate">{node.location.name}</span>
        <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
          {node.location.locationType}
        </span>
      </div>
      {isExpanded && node.children.map((child) => (
        <LocationNodeItem
          key={child.location.id}
          node={child}
          depth={depth + 1}
          defaultExpanded={false}
        />
      ))}
    </div>
  );
}

export function SpaceHierarchyTab({
  locations,
  plotLabel,
  hideEmptyState = false,
}: SpaceHierarchyTabProps) {
  const tree = useMemo(() => buildTree(locations), [locations]);

  if (locations.length === 0) {
    if (hideEmptyState) {
      return null;
    }
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Ei tiloja
      </div>
    );
  }

  return (
    <div className="py-2">
      {plotLabel && (
        <p className="text-xs text-muted-foreground mb-2 font-medium">
          {plotLabel}
        </p>
      )}
      <div className="border rounded-md p-2 max-h-[400px] overflow-y-auto">
        {tree.map((node) => (
          <LocationNodeItem
            key={node.location.id}
            node={node}
            depth={0}
            defaultExpanded={true}
          />
        ))}
      </div>
    </div>
  );
}
